/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useMemo, useReducer, useRef } from 'react'
import constate from 'constate'
import { debounceFunction } from '@/lib/utils'

import { type GenericFilterStateReturn } from '../types'

interface FilterGroupStoreState<T extends GenericFilterStateReturn<any>> {
  filterStates: Record<string, { data: T; serializer: (state: T) => unknown }>
}

interface FilterGroupStoreAction<T extends GenericFilterStateReturn<any>> {
  filterName: string
  filterState: T
  toSerializableData: (state: T) => unknown
}

export interface UseFilterGroupStoreProps {
  localStoragePreffix?: string
  onChangeStates: (filterStates: Record<string, any>) => void
}

export function useFilterGroupStore<T extends GenericFilterStateReturn<any>>({
  localStoragePreffix,
  onChangeStates,
}: UseFilterGroupStoreProps) {
  const debouncedOnChangeStates = useRef(debounceFunction(onChangeStates, 100))
  const lastSerializedObject = useRef('')

  const [state, dispatch] = useReducer(
    (state: FilterGroupStoreState<T>, action: FilterGroupStoreAction<T>) => {
      const newState = {
        ...state,
        filterStates: {
          ...state.filterStates,
          [action.filterName]: {
            data: action.filterState,
            serializer: action.toSerializableData,
          },
        },
      }

      const serializedObject = Object.fromEntries(
        Object.entries(newState.filterStates).map(([key, value]) => [
          key,
          value.serializer(value.data),
        ])
      )

      if (JSON.stringify(serializedObject) !== lastSerializedObject.current) {
        debouncedOnChangeStates.current(serializedObject)
        lastSerializedObject.current = JSON.stringify(serializedObject)
      }

      return newState
    },
    {
      filterStates: {},
    }
  )

  const buildLocalStorageKey = useCallback(
    (key: string) => {
      if (!localStoragePreffix) return key
      return `${localStoragePreffix}--${key}`
    },
    [localStoragePreffix]
  )

  const filterStatesValues = useMemo(
    () => Object.values(state.filterStates).map((filter) => filter.data),
    [state.filterStates]
  )

  return { filterStatesValues, dispatch, buildLocalStorageKey }
}

const [
  FilterGroupStoreProvider,
  useFilterStates,
  useFilterStateDispatcher,
  useBuildLocalStorageKey,
] = constate(
  useFilterGroupStore<any>,
  (value) => value.filterStatesValues,
  (value) => value.dispatch,
  (value) => value.buildLocalStorageKey
)

export {
  FilterGroupStoreProvider,
  useBuildLocalStorageKey,
  useFilterStateDispatcher,
  useFilterStates,
}
